import { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToastContext } from '../../contextStore/ToastContext';
import { getGoogleProvider } from '../../firebase/auth';
import OverlaySpinner from '../Loading/OverlaySpinner';
import './Signup.css';
import {
  handleGuestContinue,
  handleSocialSignUp,
  handleSubmit as onSubmit,
} from './signupHandlers';

const Logo = `${process.env.PUBLIC_URL || ''}/assets/images/logo.png`;

export default function Signup() {
  const { t } = useTranslation();
  const history = useHistory();
  const { addToast } = useContext(ToastContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  // "unsubscribe from newsletter" checkbox — inverts emailSubscribed
  const [noNewsletter, setNoNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) =>
    onSubmit(e, {
      name,
      email,
      phone,
      password,
      termsAccepted,
      emailSubscribed: !noNewsletter,
      setLoading,
      setErrors,
      addToast,
      history,
      t,
    });

  const onSocialSignUp = () =>
    handleSocialSignUp('google', getGoogleProvider(), {
      setLoading,
      setErrors,
      addToast,
      history,
      t,
    });

  const onGuestContinue = () => handleGuestContinue({ addToast, history, t });

  return (
    <>
      {loading && <OverlaySpinner />}
      <div className="signupPageWrapper">
        <Link to="/" className="signupHomeLink">
          ← {t('auth.backToHome')}
        </Link>
        <div className="signupParentDiv">
          <Link to="/" className="signupLogoLink">
            <img width="80" height="80" src={Logo} alt="CXB" />
          </Link>
          <form onSubmit={handleSubmit}>
            <div className="signupFormGroup">
              <label>{t('auth.fullName')}</label>
              <input
                className="input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                name="name"
                placeholder={t('auth.fullNamePlaceholder')}
              />
              {errors.name && <span className="formError">{errors.name}</span>}
            </div>
            <div className="signupFormGroup">
              <label>{t('auth.email')}</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                name="email"
                placeholder={t('auth.emailPlaceholder')}
              />
              {errors.email && (
                <span className="formError">{errors.email}</span>
              )}
            </div>
            <div className="signupFormGroup">
              <label>{t('auth.phone')}</label>
              <input
                className="input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                name="phone"
                placeholder={t('auth.phonePlaceholder')}
              />
              {errors.phone && (
                <span className="formError">{errors.phone}</span>
              )}
            </div>
            <div className="signupFormGroup">
              <label>{t('auth.password')}</label>
              <div className="signupPasswordWrap">
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  name="password"
                  placeholder={t('auth.passwordMinLength')}
                />
                <button
                  type="button"
                  className="signupPasswordToggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={t(
                    showPassword ? 'auth.hidePassword' : 'auth.showPassword'
                  )}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="formError">{errors.password}</span>
              )}
            </div>
            <div className="signupFormGroup">
              <label className="signupTermsLabel">
                <input
                  type="checkbox"
                  checked={noNewsletter}
                  onChange={(e) => {
                    setNoNewsletter(e.target.checked);
                    setEmailSubscribed(!e.target.checked);
                  }}
                />
                {t('auth.doNotSubscribeNewsletter')}
              </label>
            </div>
            <div className="signupFormGroup">
              <label className="signupTermsLabel">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                {t('auth.acceptTerms')} <Link to="/legal">{t('auth.termsAndConditions')}</Link> {t('auth.and')}{' '}
                <Link to="/legal">{t('auth.privacyPolicy')}</Link>
              </label>
              {errors.terms && (
                <span className="formError">{errors.terms}</span>
              )}
              {errors.form && <span className="formError">{errors.form}</span>}
            </div>
            <button type="submit">{t('auth.signupButton')}</button>
          </form>
          <div className="signupDivider">{t('auth.or')}</div>
          <button
            type="button"
            className="signupGoogleBtn"
            onClick={onSocialSignUp}
          >
            {t('auth.continueWithGoogle')}
          </button>
          <button
            type="button"
            className="signupGuestBtn"
            onClick={onGuestContinue}
          >
            {t('auth.continueAsGuest')}
          </button>
          <Link to="/login" className="signupLoginLink">
            {t('auth.loginButton')}
          </Link>
        </div>
      </div>
    </>
  );
}
